"use client"

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react"

export interface CourseOption {
  id: string
  title: string
  thumbnail_url: string | null
  language: string | null
  icon: string | null
  type: string
  // True when the course has at least one challenge configured. Gates the
  // "Desafio" tab in the student nav (see Header).
  hasChallenge: boolean
}

interface CurrentCourseContextValue {
  currentCourse: CourseOption | null
  courses: CourseOption[]
  setCourse: (course: CourseOption) => void
  setCourses: (courses: CourseOption[]) => void
  updateCourse: (id: string, patch: Partial<CourseOption>) => void
}

const STORAGE_KEY = "dzp_current_course_id"

const CurrentCourseContext = createContext<CurrentCourseContextValue | null>(
  null
)

export function CurrentCourseProvider({ children }: { children: ReactNode }) {
  const [courses, setCourses] = useState<CourseOption[]>([])
  const [currentCourse, setCurrentCourse] = useState<CourseOption | null>(null)

  // Rehydrate from localStorage once courses are loaded
  useEffect(() => {
    if (courses.length === 0) return
    const savedId = localStorage.getItem(STORAGE_KEY)
    const saved = savedId ? courses.find((c) => c.id === savedId) : null
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCurrentCourse(saved ?? courses[0])
  }, [courses])

  const setCourse = useCallback((course: CourseOption) => {
    setCurrentCourse(course)
    localStorage.setItem(STORAGE_KEY, course.id)
  }, [])

  const updateCourse = useCallback(
    (id: string, patch: Partial<CourseOption>) => {
      setCourses((prev) =>
        prev.map((c) => (c.id === id ? { ...c, ...patch } : c))
      )
      setCurrentCourse((prev) =>
        prev?.id === id ? { ...prev, ...patch } : prev
      )
    },
    []
  )

  return (
    <CurrentCourseContext.Provider
      value={{ currentCourse, courses, setCourse, setCourses, updateCourse }}
    >
      {children}
    </CurrentCourseContext.Provider>
  )
}

const noop = () => {}

export function useCurrentCourse() {
  const ctx = useContext(CurrentCourseContext)
  if (!ctx) {
    // Outside provider (e.g. public pages) — return safe no-op defaults
    return {
      currentCourse: null,
      courses: [],
      setCourse: noop,
      setCourses: noop,
      updateCourse: noop,
    } as CurrentCourseContextValue
  }
  return ctx
}
